import {
  Text,
  Banner,
  Checkbox,
  GreyGrey70,
  GreyGrey80,
  GreyGrey90,
  PopoverWrapper,
  Primary13SemiBold,
  Primary13Regular,
  SimpleModal,
  TextAreaField,
  Box
} from '@sequencehq/core-components'
import { FC, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { match } from 'ts-pattern'
import { PopoverRecipients } from '../../QuoteEditorControls/PopoverRecipients/PopoverRecipients'
import { PUBLISH_BODY_TEST_IDS } from './PublishQuoteModalV2/PublishQuoteModalV2.constants'
import { usePublishBodyCopy } from './usePublishBodyCopy'

type ConfirmPublishQuoteModalProps = {
  onClose: () => void
  onConfirm: (body: {
    sendRecipientsEmail: boolean
    emailMessage: string | undefined
  }) => Promise<void>
  submitting: boolean
  hasBeenPublished?: boolean
  isReadyToSign?: boolean
  recipientCountText: string
  republishingWithoutChanges?: boolean
}

/**
 * @deprecated
 */
export const ConfirmPublishQuoteModal: FC<ConfirmPublishQuoteModalProps> = ({
  hasBeenPublished,
  isReadyToSign,
  republishingWithoutChanges,
  ...props
}) => {
  const [notifyRecipient, setNotifyRecipient] = useState<boolean>(false)
  const [emailMessage, setEmailMessage] = useState<string | undefined>(
    undefined
  )
  const bodyCopy = usePublishBodyCopy()

  const { ctaText, detailText, bodyTestId } = match({
    republishingWithoutChanges,
    hasBeenPublished
  })
    .with({ republishingWithoutChanges: true }, () => ({
      ctaText: 'Republish quote',
      detailText:
        "You're about to republish this quote. This will reset the expiry date.",
      bodyTestId: PUBLISH_BODY_TEST_IDS.expiryReset
    }))
    .with({ hasBeenPublished: true }, () => ({
      ctaText: 'Publish changes',
      detailText: bodyCopy.publishChanges,
      bodyTestId: bodyCopy['data-testid']
    }))
    .otherwise(() => ({
      ctaText: 'Publish quote',
      detailText: bodyCopy.publishQuote,
      bodyTestId: bodyCopy['data-testid']
    }))

  return (
    <SimpleModal
      isOpen
      data-testid="quote.modals.publish"
      variant="dialog"
      dialogPosition="top"
      width="414px"
      title={ctaText}
      onClose={props.onClose}
      onSubmit={() => {
        void props.onConfirm({
          sendRecipientsEmail: notifyRecipient,
          emailMessage: emailMessage ? emailMessage : undefined
        })
        // The parent owns the modal lifecycle: it unmounts this modal once the
        // publish completes, so keep it open with the submitting spinner here.
        return false
      }}
      disableSubmitOnEnter
      submitButtonText={ctaText}
      submittingButtonText={ctaText}
      cancelButtonText="Cancel"
      submitting={props.submitting}
    >
      <Text
        {...Primary13Regular}
        color={GreyGrey80}
        marginBottom="16px"
        data-testid={bodyTestId}
      >
        {detailText}
      </Text>

      <Box marginBottom={notifyRecipient ? '16px' : 0}>
        <Checkbox
          isChecked={notifyRecipient}
          onChange={() => setNotifyRecipient(notify => !notify)}
          label={
            <Text
              {...Primary13Regular}
              color={GreyGrey90}
              display="flex"
              alignItems="baseline"
              gap="3px"
            >
              Notify{' '}
              <PopoverWrapper popover={<PopoverRecipients />}>
                <Text
                  as="span"
                  {...Primary13SemiBold}
                  color="inherit"
                  borderBottom={`${GreyGrey70} 1px dashed`}
                >
                  {props.recipientCountText}
                </Text>
              </PopoverWrapper>{' '}
              by email
            </Text>
          }
        />
      </Box>

      <AnimatePresence initial={false}>
        {notifyRecipient && (
          <motion.div
            initial={{ height: '0px' }}
            animate={{ height: 'fit-content' }}
            exit={{ height: '0px' }}
            style={{ overflow: 'hidden' }}
          >
            <Text {...Primary13SemiBold} marginBottom="8px" color={GreyGrey80}>
              Message (optional)
            </Text>
            <TextAreaField
              value={emailMessage}
              onChange={setEmailMessage}
              height="80px"
              color={GreyGrey80}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {isReadyToSign && (
        <Box marginTop="16px">
          <Banner variant="warning">
            All signers will receive a new signature request via email. Previous
            signature requests will be cancelled.
          </Banner>
        </Box>
      )}
    </SimpleModal>
  )
}
